import { Link } from "react-router-dom";
import { Check, MessageCircle, CalendarCheck, MapPin, ArrowUpRight, Phone, Clock, BookOpen, Navigation } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSettings } from "@/context/SettingsContext";
import { useTrackView } from "@/lib/useTrackView";
import { whatsappLink, telLink, serviceWhatsAppMessage, mapsLink } from "@/lib/contact";
import { serviceSchema, faqSchema, breadcrumbSchema, localBusiness } from "@/lib/schema";
import FinalCta from "@/sections/FinalCta";
import { ExtraSections } from "@/components/ExtraSections";

export default function ServiceDistrict({ data: p }) {
  const settings = useSettings();
  useTrackView("district", p.slug);

  const svc = p.service;
  const waMsg = serviceWhatsAppMessage(p.service_name, p.district);
  const crumbs = [
    { label: "Ana Sayfa", to: "/" },
    { label: p.district, to: `/${p.district_slug}` },
    { label: p.service_name },
  ];
  const allFaqs = [...(p.faqs || []), ...(svc?.faqs || [])];
  const mapsEmbed = `https://www.google.com/maps?q=${encodeURIComponent(`Meguiar's İzmit ${p.district} Kocaeli`)}&output=embed`;
  const schema = [
    serviceSchema({ name: p.h1, description: p.seo_description, url: `/${p.slug}`, image: p.hero_image }),
    localBusiness(settings),
    breadcrumbSchema(crumbs),
  ];
  const fq = faqSchema(allFaqs);
  if (fq) schema.push(fq);

  return (
    <>
      <Seo title={p.seo_title} description={p.seo_description} image={p.hero_image} canonicalPath={`/${p.slug}`} jsonLd={schema} />
      <PageHero overline={`${p.district} • Kocaeli`} title={p.h1} subtitle={p.intro} image={p.hero_image} breadcrumbs={crumbs}>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to={`/randevu?hizmet=${encodeURIComponent(p.service_name)}`} className="inline-flex items-center gap-2 bg-gold px-6 py-3.5 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300" data-testid="district-randevu-btn">
            <CalendarCheck className="h-[18px] w-[18px]" /> Hızlı Randevu Al
          </Link>
          <a href={whatsappLink(settings, waMsg)} target="_blank" rel="noopener noreferrer" data-testid="district-whatsapp-btn" className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.04] px-6 py-3.5 font-oswald text-sm font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:border-gold/50">
            <MessageCircle className="h-[18px] w-[18px] text-gold" /> WhatsApp'tan Yaz
          </a>
        </div>
      </PageHero>

      <section className="obsidian py-16 sm:py-24">
        <div className="container-x grid gap-12 lg:grid-cols-[1.6fr_0.9fr]">
          <div className="space-y-14">
            <Reveal>
              <div className="flex items-center gap-3"><span className="font-oswald text-sm font-bold tracking-[0.2em] text-gold">01</span><h2 className="font-display text-2xl font-medium text-white sm:text-3xl">Neden {p.district} için Meguiar's İzmit?</h2></div>
              <p className="mt-4 text-base leading-relaxed text-neutral-300">{p.why_us}</p>
            </Reveal>

            <ExtraSections sections={p.sections} waMessage={waMsg} />

            {svc?.what_is && (
              <Reveal>
                <div className="flex items-center gap-3"><span className="font-oswald text-sm font-bold tracking-[0.2em] text-gold">02</span><h2 className="font-display text-2xl font-medium text-white sm:text-3xl">{p.service_name} hakkında</h2></div>
                <p className="mt-4 text-base leading-relaxed text-neutral-300">{svc.what_is}</p>
              </Reveal>
            )}

            {svc?.benefits?.length > 0 && (
              <Reveal>
                <div className="border border-gold/25 bg-gold/[0.04] p-6">
                  <h3 className="font-display text-xl font-medium text-white">{p.service_name} faydaları</h3>
                  <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                    {svc.benefits.map((b, i) => (<li key={i} className="flex items-start gap-3 text-sm text-neutral-200"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {b}</li>))}
                  </ul>
                </div>
              </Reveal>
            )}

            {/* Bu ilçede yaptığımız uygulamalar */}
            {p.applications?.length > 0 && (
              <Reveal>
                <div className="flex items-center gap-3"><span className="font-oswald text-sm font-bold tracking-[0.2em] text-gold">03</span><h2 className="font-display text-2xl font-medium text-white sm:text-3xl">{p.district}'te yaptığımız uygulamalar</h2></div>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {p.applications.map((a) => (
                    <Link key={a.slug} to={`/uygulamalar/${a.slug}`} data-testid={`district-application-${a.slug}`} className="group flex items-center gap-4 overflow-hidden border border-white/10 bg-neutral-900 p-3 transition-colors hover:border-gold/40">
                      <img src={a.cover_image || a.after_image} alt={a.title} loading="lazy" className="h-20 w-24 shrink-0 object-cover grayscale-[0.2] transition group-hover:grayscale-0" />
                      <span className="min-w-0">
                        {a.brand && <span className="block font-oswald text-[10px] uppercase tracking-[0.15em] text-gold">{a.brand} {a.model} {a.year ? `• ${a.year}` : ""}</span>}
                        <span className="block font-medium text-white transition-colors group-hover:text-gold line-clamp-2">{a.title}</span>
                        {a.duration && <span className="mt-1 inline-flex items-center gap-1.5 text-xs text-neutral-500"><Clock className="h-3 w-3" /> {a.duration}</span>}
                      </span>
                    </Link>
                  ))}
                </div>
              </Reveal>
            )}

            {svc?.process?.length > 0 && (
              <Reveal>
                <div className="flex items-center gap-3"><span className="font-oswald text-sm font-bold tracking-[0.2em] text-gold">04</span><h2 className="font-display text-2xl font-medium text-white sm:text-3xl">Uygulama süreci</h2></div>
                <ol className="mt-5 space-y-2.5">
                  {svc.process.map((pr, i) => (
                    <li key={i} className="flex gap-4 border border-white/10 bg-white/[0.02] p-5 transition-colors hover:border-gold/30">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-gold font-display font-semibold text-black">{i + 1}</span>
                      <span><span className="block font-display text-lg font-medium text-white">{pr.title}</span><span className="mt-1 block text-sm text-neutral-400">{pr.desc}</span></span>
                    </li>
                  ))}
                </ol>
              </Reveal>
            )}

            {/* İlgili rehber yazıları */}
            {p.related_blogs?.length > 0 && (
              <Reveal>
                <div className="flex items-center gap-3"><span className="font-oswald text-sm font-bold tracking-[0.2em] text-gold">05</span><h2 className="font-display text-2xl font-medium text-white sm:text-3xl">İlgili rehber yazıları</h2></div>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {p.related_blogs.map((b) => (
                    <Link key={b.slug} to={`/blog/${b.slug}`} data-testid={`district-blog-${b.slug}`} className="group flex items-center gap-4 overflow-hidden border border-white/10 bg-neutral-900 p-3 transition-colors hover:border-gold/40">
                      <img src={b.cover_image} alt={b.title} loading="lazy" className="h-20 w-24 shrink-0 object-cover grayscale-[0.2] transition group-hover:grayscale-0" />
                      <span className="min-w-0"><span className="inline-flex items-center gap-1.5 font-oswald text-[10px] uppercase tracking-[0.15em] text-gold"><BookOpen className="h-3 w-3" /> Rehber</span><span className="mt-0.5 block font-medium leading-snug text-white transition-colors group-hover:text-gold line-clamp-2">{b.title}</span></span>
                    </Link>
                  ))}
                </div>
              </Reveal>
            )}

            {allFaqs.length > 0 && (
              <Reveal>
                <div className="flex items-center gap-3"><span className="font-oswald text-sm font-bold tracking-[0.2em] text-gold">06</span><h2 className="font-display text-2xl font-medium text-white sm:text-3xl">Sık sorulan sorular</h2></div>
                <Accordion type="single" collapsible className="mt-4 w-full border-t border-white/10">
                  {allFaqs.map((f, i) => (
                    <AccordionItem key={i} value={`f-${i}`} className="border-white/10">
                      <AccordionTrigger className="py-4 text-left font-display text-base font-medium text-white hover:no-underline hover:text-gold [&[data-state=open]]:text-gold">{f.question}</AccordionTrigger>
                      <AccordionContent className="pb-4 text-sm text-neutral-400">{f.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-28 lg:h-fit">
            <div className="border border-white/10 bg-gradient-to-b from-neutral-900 to-[#070707] p-6">
              <h3 className="font-display text-xl font-medium text-white">{p.district}'te randevu</h3>
              <p className="mt-2 text-sm text-neutral-400">Ücretsiz değerlendirme için hemen ulaşın.</p>
              <div className="mt-5 space-y-2.5">
                <Link to={`/randevu?hizmet=${encodeURIComponent(p.service_name)}`} className="flex w-full items-center justify-center gap-2 bg-gold px-5 py-3.5 font-oswald text-sm font-bold uppercase tracking-[0.1em] text-black transition-colors hover:bg-gold-300"><CalendarCheck className="h-[18px] w-[18px]" /> Randevu Al</Link>
                <a href={whatsappLink(settings, waMsg)} target="_blank" rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 border border-white/15 px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:border-gold/40"><MessageCircle className="h-[18px] w-[18px] text-gold" /> WhatsApp</a>
                <a href={telLink(settings)} className="flex w-full items-center justify-center gap-2 border border-white/15 px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:border-gold/40"><Phone className="h-[18px] w-[18px] text-gold" /> {settings?.phone}</a>
              </div>
            </div>

            <div className="overflow-hidden border border-white/10">
              <iframe title={`${p.district} konum`} src={mapsEmbed} className="h-56 w-full grayscale-[0.4] contrast-[1.05]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              <a href={mapsLink(settings)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-black px-5 py-3.5 font-oswald text-xs uppercase tracking-[0.15em] text-white transition-colors hover:text-gold"><Navigation className="h-4 w-4 text-gold" /> Yol Tarifi Al</a>
            </div>

            {svc && (
              <Link to={`/hizmetler/${p.ref_service}`} data-testid="district-service-link" className="group flex items-center justify-between gap-2 border border-white/10 bg-white/[0.02] p-5 transition-colors hover:border-gold/30">
                <span><span className="block text-xs text-neutral-500">Hizmet detayı</span><span className="font-display font-medium text-white transition-colors group-hover:text-gold">{p.service_name} hakkında her şey</span></span>
                <ArrowUpRight className="h-5 w-5 text-gold" />
              </Link>
            )}

            {p.other_services?.length > 0 && (
              <div className="border border-white/10 bg-white/[0.02] p-6">
                <h3 className="font-display text-lg font-medium text-white">{p.district}'te diğer hizmetler</h3>
                <ul className="mt-4 space-y-2">
                  {p.other_services.slice(0, 8).map((o) => (
                    <li key={o.slug}><Link to={`/${o.slug}`} data-testid={`district-other-${o.slug}`} className="group flex items-center justify-between gap-2 border border-white/10 px-4 py-2.5 text-sm text-neutral-200 transition-colors hover:border-gold/40 hover:text-gold">{o.service_name} <ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100" /></Link></li>
                  ))}
                </ul>
              </div>
            )}

            {p.nearby?.length > 0 && (
              <div className="border border-white/10 bg-white/[0.02] p-6">
                <h3 className="font-display text-lg font-medium text-white">Yakın ilçeler</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {p.nearby.map((n) => (<Link key={n.slug} to={`/${n.slug}`} data-testid={`district-nearby-${n.slug}`} className="inline-flex items-center gap-1.5 border border-white/10 px-3 py-2 text-sm text-neutral-300 transition-colors hover:border-gold/40 hover:text-gold"><MapPin className="h-3.5 w-3.5 text-gold/70" /> {n.district}</Link>))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
